import styled from '@emotion/styled'
import { useState } from 'react'

import {
  AssetInformationModal,
  ExDateDistribution,
  ExDateReinvestment,
  LinkButton,
  Space,
  Tabs,
  Typography
} from '../../../components/core-v2'
import ToastNotification from '../../../components/core-v2/atoms/toast-notification/toastNotification'
import AttributeAlt from '../../../components/core-v2/molecules/attribute-alt/attribute-alt'
import { actions, ExDateReinvestmentMapping } from '../state/funds-slice'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import FundExDatesView from '../templates/fund-exdates-view'
import { isPreviousExDate } from '../types/mapper'

const StyledFixScrollingArea = styled.div`
  overflow-y: scroll;
  height: calc(100vh - 251px);
  background: var(--White98);
`

const StyledMaxWidthArea = styled(Space)`
  max-width: 1000px;
`

const FundDetailsPanel = () => {
  const [selectedTab, setSelectedTab] = useState<string>('upcoming')
  const [showAssetDetails, setShowAssetDetails] = useState<boolean>(false)

  const state = useAppSelector(x => x.funds)
  const { selectedFund } = state
  const upcomingDates = state.exDateSchedules.filter(item => !isPreviousExDate(item))
  const expiredDates = state.exDateSchedules.filter(item => isPreviousExDate(item))
  const dispatch = useAppDispatch()

  const onExDateSelection = (selectedDate: string) => {
    dispatch(actions.setSelectedExDate(selectedDate))
  }
  const onDistributionSubmit = (updatedDistribution: ExDateDistribution) => {
    dispatch(actions.setDistribution(updatedDistribution))
  }
  const onReinvestmentSubmit = (updatedReinvestment: ExDateReinvestment) => {
    dispatch(actions.setReinvestment(updatedReinvestment))
  }
  const onReinvestmentAssign = (reinvestmentMapping: ExDateReinvestmentMapping) => {
    dispatch(actions.assignReinvestmentToDate(reinvestmentMapping))
  }
  const onNotificationClose = () => {
    dispatch(actions.resetExDateNotification())
  }

  const renderAction = () => (
    <Space ml={4}>
      <LinkButton underline onClick={() => setShowAssetDetails(true)}>
        View asset details
      </LinkButton>
    </Space>
  )

  const renderAttributeAlt = (label: string, value: string) => (
    <AttributeAlt label={label} value={value} />
  )

  if (selectedFund) {
    return (
      <>
        <AssetInformationModal
          assetId={selectedFund?.assetId}
          isOpen={showAssetDetails}
          onClose={() => setShowAssetDetails(undefined)}
        />
        <Space mx={5}>
          <Space isflex flexDirection="row" pt={5}>
            <Typography fontSize="15px" fontWeight="bold" letterSpacing="0px" variant="b3">
              {selectedFund.name}
            </Typography>
            {renderAction()}
          </Space>
          <Space isflex flexDirection="row" pt={5}>
            {renderAttributeAlt('Fund manager', selectedFund.name)}
            <Space pl={5}>
              {renderAttributeAlt(
                'Finswitch fund code',
                (selectedFund as any).externalIdentifiers?.fundCode
              )}
            </Space>
          </Space>
        </Space>
        <Tabs.List className="mt-4 mx-3" spacing="md">
          <Tabs.Tab
            isSelected={selectedTab === 'upcoming'}
            value="upcoming"
            onClick={() => setSelectedTab('upcoming')}
          >
            Upcoming Ex-Dates
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={selectedTab === 'previous'}
            value="previous"
            onClick={() => setSelectedTab('previous')}
          >
            Previous Ex-Dates
          </Tabs.Tab>
        </Tabs.List>
        <StyledFixScrollingArea>
          <StyledMaxWidthArea m={3}>
            {selectedTab === 'upcoming' && (
              <Tabs.Panel value="upcoming">
                <FundExDatesView
                  arePreviousDates={false}
                  assetId={selectedFund.assetId}
                  dates={upcomingDates}
                  errors={state.exDateSaveErrors}
                  expectedReinvestmentData={state.expectedReinvestmentData}
                  fetchErrors={state.errors}
                  selectedDate={state.selectedExDate}
                  unmappedReinvestments={state.unmappedReinvestments}
                  onDistributionSubmit={onDistributionSubmit}
                  onReinvestmentAssign={onReinvestmentAssign}
                  onReinvestmentSubmit={onReinvestmentSubmit}
                  onSelection={onExDateSelection}
                />
              </Tabs.Panel>
            )}
            {selectedTab === 'previous' && (
              <Tabs.Panel value="previous">
                <FundExDatesView
                  arePreviousDates
                  assetId={selectedFund.assetId}
                  dates={expiredDates}
                  errors={undefined}
                  expectedReinvestmentData={state.expectedReinvestmentData}
                  fetchErrors={state.errors}
                  selectedDate={state.selectedExDate}
                  unmappedReinvestments={[]}
                  onSelection={onExDateSelection}
                />
              </Tabs.Panel>
            )}
          </StyledMaxWidthArea>
          {state && state.exDateNotification && (
            <ToastNotification
              open
              description={state.exDateNotification}
              notificationType="success"
              title=""
              onClose={() => onNotificationClose()}
            />
          )}
        </StyledFixScrollingArea>
      </>
    )
  }
  return null
}

export default FundDetailsPanel
